'use client'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBan,
  faBoxesStacked,
  faUserGroup,
  faWindowMaximize,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import type { BestPlaceData } from '@/helpers/cms/contentTypes'

const StyledSection = styled.section`
  background: ${Colors.Semantic.Neutral.light_grey};
  padding: 3rem 1.5rem;

  @media ${Breakpoints.min.medium} {
    padding: 4rem 3rem;
  }
`

const Content = styled.div`
  width: 100%;
  max-width: var(--maxPageWidth);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
`

const PreTitle = styled.h2`
  ${Typography.Subtitle2};
  color: ${Colors.Semantic.Info.highlight};
  text-transform: uppercase;
  letter-spacing: 0.07rem;
`

const Title = styled.h3`
  ${Typography.Title3};
  color: ${Colors.Semantic.Neutral.light_black};

  @media ${Breakpoints.min.medium} {
    ${Typography.Title2};
  }
`

const CardContainer = styled.div`
  display: grid;
  gap: 3rem;

  @media ${Breakpoints.min.medium} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${Breakpoints.min.large} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  flex: 1;
  text-align: center;
  color: ${Colors.Semantic.Neutral.light_black};
`

const StyledFAIcon = styled(FontAwesomeIcon)<{ $flipped?: boolean }>`
  color: ${Colors.Semantic.Info.highlight};
  width: 2.5rem;
  height: 2.5rem;
  transform: ${({ $flipped }) => ($flipped ? 'scaleX(-1)' : 'none')};
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const CardTitle = styled.h4`
  ${Typography.Subtitle1};
`

const CardText = styled.p`
  ${Typography.Body2};
`

/**
 * The icons an editor can choose, keyed by the values the CMS stores.
 *
 * A closed set on both sides: these are build-time imports, so an arbitrary name would mean
 * shipping the whole icon library. Adding one means an option in cms-portal and an entry here.
 */
const ICONS: Record<string, IconDefinition | undefined> = {
  windowMaximize: faWindowMaximize,
  boxesStacked: faBoxesStacked,
  ban: faBan,
  userGroup: faUserGroup,
}

const FALLBACK_PRE_TITLE = 'Source Inventory for Resale'
const FALLBACK_TITLE = 'B-Stock: The best place to find inventory for resale'
const FALLBACK_CARDS = [
  {
    icon: faWindowMaximize,
    title: 'No middleman',
    text: 'Buy direct from retailers through their official liquidation auction marketplaces.',
  },
  {
    icon: faBoxesStacked,
    title: 'All lot sizes, categories',
    text: 'Source inventory for resale across all lot sizes, product categories, and price points.',
  },
  {
    icon: faBan,
    iconFlipped: true,
    title: 'No surprises',
    text: "From detailed listings of liquidation pallets to itemized manifests, you'll know the quantity.",
  },
  {
    icon: faUserGroup,
    iconFlipped: true,
    title: "We've got your back!",
    text: 'A dedicated customer service team and education center to help you grow your business through wholesale liquidators.',
  },
]

export default function BestPlaceToPurchase({
  data,
}: {
  /** CMS copy when the marketplace-content flag is on and this section is filled in. */
  data?: BestPlaceData | null
}) {
  // Card-level fallback is separate from title-level: a group with headings but no cards is a
  // half-finished edit, and showing the shipped cards under the new headings is better than a
  // section with a title and nothing beneath it.
  const cmsCards = data?.cards.length
    ? data.cards
        .map((card) => {
          const icon = ICONS[card.icon]
          if (!icon) return null
          return {
            icon,
            iconFlipped: card.iconFlipped,
            title: card.title,
            text: card.body,
          }
        })
        .filter((card) => card != null)
    : null

  const cards = cmsCards?.length ? cmsCards : FALLBACK_CARDS

  return (
    <StyledSection data-testid="best-place">
      <Content>
        <Titles>
          <PreTitle>{data?.preTitle ?? FALLBACK_PRE_TITLE}</PreTitle>
          <Title>{data?.title ?? FALLBACK_TITLE}</Title>
        </Titles>
        <CardContainer>
          {cards.map(({ icon, iconFlipped, title, text }) => (
            <Card key={title}>
              <StyledFAIcon icon={icon} $flipped={iconFlipped} />
              <CardContent>
                <CardTitle>{title}</CardTitle>
                <CardText>{text}</CardText>
              </CardContent>
            </Card>
          ))}
        </CardContainer>
      </Content>
    </StyledSection>
  )
}
